import type { CompanyStatus } from "../domain/status";
import styles from "./CompanyAvatar.module.css";
import { StatusGlyph } from "./StatusGlyph";

export const AVATAR_SIZES = [32, 36, 40, 44, 52] as const;
export type AvatarSize = (typeof AVATAR_SIZES)[number];
const PALETTE_SIZE = 10;

/** Two-letter monogram: first letters of the first two words, else the first two letters. */
export function monogram(name: string): string {
  const words = name
    .replace(/\b(bv|bvba|nv|vzw|comm\.?v|srl|sa)\b\.?/gi, "")
    .split(/[\s\-–&/.]+/)
    .filter((word) => /[\p{L}\p{N}]/u.test(word));
  const [first = "", second = ""] = words;
  const letters = second ? first.charAt(0) + second.charAt(0) : first.slice(0, 2);
  return letters.toLocaleUpperCase("nl-BE") || "?";
}

/** Palette index from the company id (FNV-1a); the API may send `avatar_hue` instead. */
export function avatarHue(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % PALETTE_SIZE;
}

export interface CompanyAvatarProps {
  id: string;
  name: string;
  hue?: number;
  status?: CompanyStatus;
  size?: AvatarSize;
}

/** Decorative: the company name is always shown next to it. */
export function CompanyAvatar({ id, name, hue, status, size = 36 }: CompanyAvatarProps) {
  const index = ((hue ?? avatarHue(id)) % PALETTE_SIZE + PALETTE_SIZE) % PALETTE_SIZE;
  return (
    <span
      aria-hidden="true"
      className={styles.avatar}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.27),
        fontSize: Math.round(size * 0.35),
        background: `var(--av-${index}-bg)`,
        color: `var(--av-${index}-fg)`,
      }}
    >
      {monogram(name)}
      {status && (
        <span className={styles.badge}>
          <StatusGlyph status={status} size={12} />
        </span>
      )}
    </span>
  );
}
